/**
 * Search Results Page
 *
 * Display search results with filters
 * Task: T4.6.2 - Search Results page
 */

import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Box,
  TextField,
  Select,
  Spinner,
  Banner,
} from '@shopify/polaris';
import { SearchIcon } from '@shopify/polaris-icons';
import { ProductGrid } from '../components/product/ProductGrid';
import { Pagination } from '../components/common/Pagination';
import type { ProductCardData } from '../components/product/ProductCard';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

const sortOptions = [
  { label: 'Relevance', value: 'relevance' },
  { label: 'Price: Low to High', value: 'price_asc' },
  { label: 'Price: High to Low', value: 'price_desc' },
  { label: 'Newest', value: 'newest' },
  { label: 'Name: A-Z', value: 'name_asc' },
];

export function SearchResultsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const query = searchParams.get('q') || '';
  const page = parseInt(searchParams.get('page') || '1', 10);
  const sortBy = searchParams.get('sortBy') || 'relevance';

  const [searchInput, setSearchInput] = useState(query);
  const [products, setProducts] = useState<ProductCardData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
    limit: 12,
  });

  // Fetch search results
  useEffect(() => {
    const controller = new AbortController();

    const fetchResults = async () => {
      if (!query.trim()) {
        setProducts([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          q: query,
          page: page.toString(),
          limit: pagination.limit.toString(),
          sortBy,
        });

        const response = await fetch(`${API_URL}/products/search?${params}`, {
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error('Search failed');
        }

        const data = await response.json();

        setProducts(data.data || []);
        setPagination((prev) => ({
          ...prev,
          total: data.meta?.total || 0,
          totalPages: Math.ceil((data.meta?.total || 0) / prev.limit),
        }));
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }
        setError('Failed to search products. Please try again.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchResults();

    return () => controller.abort();
  }, [query, page, sortBy, pagination.limit]);

  // Handle new search
  const handleSearch = useCallback(() => {
    if (searchInput.trim()) {
      setSearchParams({ q: searchInput, page: '1', sortBy });
    }
  }, [searchInput, sortBy, setSearchParams]);

  // Handle sort change
  const handleSortChange = useCallback(
    (value: string) => {
      setSearchParams({ q: query, page: '1', sortBy: value });
    },
    [query, setSearchParams]
  );

  // Handle page change
  const handlePageChange = useCallback(
    (newPage: number) => {
      setSearchParams({ q: query, page: newPage.toString(), sortBy });
    },
    [query, sortBy, setSearchParams]
  );

  // Handle wishlist
  const handleAddToWishlist = useCallback((productId: string) => {
    // TODO: Implement wishlist functionality
    console.log('Add to wishlist:', productId);
  }, []);

  // Handle key press for search
  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSearch();
      }
    },
    [handleSearch]
  );

  return (
    <Page
      title={query ? `Search results for "${query}"` : 'Search Products'}
      backAction={{ content: 'Back', onAction: () => navigate(-1) }}
    >
      <Layout>
        {/* Search Bar */}
        <Layout.Section>
          <Card>
            <Box padding="400">
              <div onKeyDown={handleKeyPress}>
                <InlineStack gap="300" blockAlign="end">
                  <Box minWidth="400px">
                    <TextField
                      label="Search"
                      labelHidden
                      value={searchInput}
                      onChange={setSearchInput}
                      placeholder="Search for products..."
                      prefix={<SearchIcon />}
                      autoComplete="off"
                      clearButton
                      onClearButtonClick={() => setSearchInput('')}
                    />
                  </Box>
                  <button
                    onClick={handleSearch}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: 'var(--p-color-bg-fill-brand)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                    }}
                  >
                    Search
                  </button>
                </InlineStack>
              </div>
            </Box>
          </Card>
        </Layout.Section>

        {/* Error State */}
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        {/* Loading State */}
        {loading && (
          <Layout.Section>
            <Box padding="800">
              <BlockStack align="center">
                <Spinner size="large" />
                <Text as="p" tone="subdued">Searching...</Text>
              </BlockStack>
            </Box>
          </Layout.Section>
        )}

        {/* No Query State */}
        {!loading && !query && (
          <Layout.Section>
            <Card>
              <Box padding="800">
                <BlockStack align="center" gap="400">
                  <Text as="p" variant="headingLg">
                    What are you looking for?
                  </Text>
                  <Text as="p" tone="subdued">
                    Enter a search term above to find products
                  </Text>
                </BlockStack>
              </Box>
            </Card>
          </Layout.Section>
        )}

        {/* Results */}
        {!loading && query && (
          <>
            {/* Results Header */}
            <Layout.Section>
              <InlineStack align="space-between" blockAlign="center">
                <Text as="p" variant="bodyMd">
                  {pagination.total > 0
                    ? `Found ${pagination.total} products`
                    : 'No products found'}
                </Text>

                {products.length > 0 && (
                  <Box minWidth="180px">
                    <Select
                      label="Sort by"
                      labelHidden
                      options={sortOptions}
                      value={sortBy}
                      onChange={handleSortChange}
                    />
                  </Box>
                )}
              </InlineStack>
            </Layout.Section>

            {/* Product Grid */}
            <Layout.Section>
              {products.length > 0 ? (
                <Card>
                  <Box padding="400">
                    <ProductGrid
                      products={products}
                      onAddToWishlist={handleAddToWishlist}
                      columns={{ xs: 1, sm: 2, md: 3, lg: 4 }}
                    />

                    {pagination.totalPages > 1 && (
                      <Pagination
                        currentPage={page}
                        totalPages={pagination.totalPages}
                        totalItems={pagination.total}
                        itemsPerPage={pagination.limit}
                        onPageChange={handlePageChange}
                        showItemsPerPage={false}
                      />
                    )}
                  </Box>
                </Card>
              ) : (
                <Card>
                  <Box padding="800">
                    <BlockStack align="center" gap="400">
                      <Text as="p" variant="headingLg">
                        No products found
                      </Text>
                      <Text as="p" tone="subdued">
                        Try different keywords or browse our categories
                      </Text>
                      <button
                        onClick={() => navigate('/categories')}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: 'var(--p-color-bg-fill-brand)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                        }}
                      >
                        Browse Categories
                      </button>
                    </BlockStack>
                  </Box>
                </Card>
              )}
            </Layout.Section>
          </>
        )}
      </Layout>
    </Page>
  );
}
